import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  User, CreditCard, Calendar, TrendingUp, Shield, Trash2, Key,
  Crown, Check, X, Home, Loader2, AlertCircle, ArrowLeft, ShieldCheck, ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Tooltip from '../components/Tooltip';
import ProfileCompletion from '../components/ProfileCompletion';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MyProfile = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    // Wait for auth context to finish loading
    if (authLoading) return;
    
    // Check if user is authenticated via either user object or token
    const token = localStorage.getItem('if_auth_token');
    
    if (!user && !token) {
      navigate('/auth');
      return;
    }
    fetchProfileData();
  }, [user, authLoading, navigate]);

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem('if_auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await axios.get(`${BACKEND_URL}/api/profile`, {
        withCredentials: true,
        headers
      });
      setProfileData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        navigate('/auth');
        return;
      }
      toast({
        title: "Error",
        description: "Failed to load profile data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords don't match",
        variant: "destructive"
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/profile/change-password`, {
        new_password: newPassword
      }, {
        withCredentials: true
      });

      toast({
        title: "Success!",
        description: "Password changed successfully"
      });
      setShowPasswordChange(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to change password",
        variant: "destructive"
      });
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/profile/delete`, {
        withCredentials: true
      });

      toast({
        title: "Account Deleted",
        description: "Your account has been permanently deleted"
      });
      
      logout();
      navigate('/');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete account",
        variant: "destructive"
      });
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'enterprise': return 'bg-gradient-to-r from-purple-600 to-pink-600 text-white';
      case 'pro': return 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white';
      default: return 'bg-slate-200 text-slate-700';
    }
  };

  // Show loading while auth is being checked
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <User className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold">My Profile</h1>
              <p className="text-sm text-slate-600">Manage your account settings</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Tooltip content="Go back">
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Tooltip>
            <Tooltip content="Return to dashboard">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Tooltip>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Profile Completion Progress */}
        <div className="mb-6">
          <ProfileCompletion user={user} profileData={profileData} />
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column - Account Info */}
          <div className="md:col-span-2 space-y-6">
            {/* Account Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-600" />
                  Account Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">IF Member ID</label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      value={profileData?.if_user_id || 'Loading...'} 
                      disabled 
                      className="font-mono bg-purple-50 text-purple-900 font-semibold text-lg" 
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(profileData?.if_user_id || '');
                        toast({
                          title: "Copied!",
                          description: "IF Member ID copied to clipboard"
                        });
                      }}
                      className="shrink-0"
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Your unique Idea Fountain ID (format: #IF-XXXX)</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Name</label>
                  <Input value={profileData?.name || user?.name || ''} disabled className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Email</label>
                  <Input value={profileData?.email || user?.email || ''} disabled className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Member Since</label>
                  <Input 
                    value={profileData?.created_at ? new Date(profileData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} 
                    disabled 
                    className="mt-1" 
                  />
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-purple-600" />
                  Security
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Two-Factor Authentication Link */}
                <div 
                  onClick={() => navigate('/settings/security')}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 cursor-pointer hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Two-Factor Authentication</h4>
                      <p className="text-sm text-slate-600">Add an extra layer of security to your account</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" />
                </div>

                {/* Password Change Section */}
                {!showPasswordChange ? (
                  <Button onClick={() => setShowPasswordChange(true)} variant="outline">
                    Change Password
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-slate-700">New Password</label>
                      <Input 
                        type="password" 
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-700">Confirm Password</label>
                      <Input 
                        type="password" 
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handlePasswordChange} className="bg-purple-600">
                        Update Password
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setShowPasswordChange(false);
                        setNewPassword('');
                        setConfirmPassword('');
                      }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Usage Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Usage This Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-2xl font-bold text-blue-900">
                      {profileData?.sessions_this_month || 0}/{profileData?.session_limit === -1 ? '∞' : profileData?.session_limit || 3}
                    </div>
                    <div className="text-sm text-blue-700">IF Sessions</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="text-2xl font-bold text-purple-900">
                      {profileData?.evaluations_this_month || 0}/{profileData?.evaluation_limit === -1 ? '∞' : profileData?.evaluation_limit || 5}
                    </div>
                    <div className="text-sm text-purple-700">Evaluations</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delete Account - Danger Zone */}
            <Card className="border-2 border-red-200 bg-red-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-900">
                  <AlertCircle className="w-5 h-5" />
                  Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!showDeleteConfirm ? (
                  <div>
                    <p className="text-sm text-slate-700 mb-4">
                      Once you delete your account, there is no going back. All your data will be permanently deleted.
                    </p>
                    <Button 
                      variant="destructive" 
                      onClick={() => setShowDeleteConfirm(true)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Account
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border-2 border-red-300">
                      <p className="font-semibold text-red-900 mb-2">⚠️ Are you absolutely sure?</p>
                      <p className="text-sm text-slate-700">
                        This will permanently delete your account and all associated data including sessions, evaluations, and subscriptions.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="destructive" 
                        onClick={handleDeleteAccount}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Yes, Delete My Account
                      </Button>
                      <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Subscription */}
          <div className="space-y-6">
            {/* Subscription Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-purple-600" />
                  Subscription
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <Badge className={`text-lg px-4 py-2 ${getTierBadgeColor(profileData?.subscription_tier || 'free')}`}>
                    {(profileData?.subscription_tier || 'free').toUpperCase()}
                  </Badge>
                  <p className="text-sm text-slate-600 mt-2">
                    {profileData?.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} Plan
                  </p>
                </div>

                {profileData?.subscription_tier !== 'free' && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Next Billing:</span>
                      <span className="font-semibold">
                        {profileData?.next_billing_date 
                          ? new Date(profileData.next_billing_date).toLocaleDateString() 
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Status:</span>
                      <Badge variant="outline" className="text-green-700 border-green-300">
                        {profileData?.subscription_status || 'Active'}
                      </Badge>
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white"
                  onClick={() => navigate('/pricing')}
                >
                  {profileData?.subscription_tier === 'free' ? 'Upgrade Now' : 'Manage Plan'}
                </Button>
              </CardContent>
            </Card>

            {/* Payment Method */}
            {profileData?.subscription_tier !== 'free' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                    Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-slate-600" />
                        <span className="font-semibold">•••• {profileData?.last4 || '4242'}</span>
                      </div>
                      <span className="text-sm text-slate-600">{profileData?.card_brand || 'Visa'}</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Expires {profileData?.card_exp || '12/25'}
                    </p>
                  </div>
                  <Button variant="outline" className="w-full mt-4" size="sm">
                    Update Card
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
